/**
 * SF-156 Component 2: tab-aware kingdom totals for the mobile Dossier roster header. Pure
 * aggregation, unit-tested independent of rendering (mirrors `buildingsPercent.ts`/
 * `relationsPresentation.ts`'s precedent).
 *
 * Every totals row carries an explicit "{scoutedCount}/{scoutedOf} scouted" caveat, since a total
 * can only sum what's actually been spied on. `scoutedCount`/`scoutedOf` are tab-specific
 * denominators — Resources/Effects are Crystal-Ball-scoped, Military is Spy-on-Military-scoped —
 * never one shared number across tabs.
 */

import { COLORS } from '../constants/theme';
import { formatNumber } from './format';
import { classifyEffect } from './effectClassification';
import type { DossierProvinceRow } from '../types/dossier';
import type { CrystalBallData, SpyOnMilitaryData } from '../types/intel';

export type DossierTabId = 'resources' | 'effects' | 'military';

export interface DossierTotalsCell {
  label: string;
  value: string;
  color: string;
}

export interface DossierTotals {
  cells: DossierTotalsCell[];
  /** Tab-specific "on file" denominator (CB-scoped for Resources/Effects, SoM-scoped for Military). */
  scoutedCount: number;
  scoutedOf: number;
  caveat: string;
}

// Colors not already exposed as named COLORS tokens (matches the Crystal Ball/Effects category
// palette used elsewhere: MobileCrystalBallCard.tsx's CB_FG/OFF_CLR, EffectsTab.tsx's CATEGORY_COLORS).
const CB_FG = '#b89fe0';
const OFF_CLR = '#e8a04f';

/** '—' when nothing has been scouted (informative-absence, not a measured zero) — see Component 2. */
function fmtOrDash(scoutedCount: number, value: number): string {
  return scoutedCount === 0 ? '—' : formatNumber(value);
}

function caveatText(scoutedCount: number, scoutedOf: number): string {
  return `${scoutedCount}/${scoutedOf} scouted — totals reflect scouted provinces only`;
}

function computeResourcesTotals(provinces: DossierProvinceRow[]): DossierTotals {
  const scoutedOf = provinces.length;
  const scouted = provinces.filter((p) => p.latestCb != null);
  const scoutedCount = scouted.length;
  const sum = (f: (cb: CrystalBallData) => number) =>
    scouted.reduce((s, p) => s + f(p.latestCb!.data as CrystalBallData), 0);

  return {
    cells: [
      { label: 'Total Gold (gc)', value: fmtOrDash(scoutedCount, sum((cb) => cb.money)), color: COLORS.gold },
      { label: 'Total Food (bushels)', value: fmtOrDash(scoutedCount, sum((cb) => cb.food)), color: COLORS.text.primary },
      { label: 'Total Runes', value: fmtOrDash(scoutedCount, sum((cb) => cb.runes)), color: CB_FG },
    ],
    scoutedCount,
    scoutedOf,
    caveat: caveatText(scoutedCount, scoutedOf),
  };
}

function computeMilitaryTotals(provinces: DossierProvinceRow[]): DossierTotals {
  const scoutedOf = provinces.length;
  // scoutedCount is report-presence (latestSom != null) — distinct from, and not narrowed by,
  // which members' offensePoints/defensePoints happen to be non-null (a member can have an SoM
  // report on file with points still null).
  const scoutedSom = provinces.filter((p) => p.latestSom != null);
  const scoutedCount = scoutedSom.length;

  const withOffense = provinces.filter((p) => (p.latestSom?.data as SpyOnMilitaryData | undefined)?.offensePoints != null);
  const totalRawOff = withOffense.reduce((s, p) => s + (p.latestSom!.data as SpyOnMilitaryData).offensePoints!, 0);

  const withDefense = provinces.filter((p) => (p.latestSom?.data as SpyOnMilitaryData | undefined)?.defensePoints != null);
  const totalRawDef = withDefense.reduce((s, p) => s + (p.latestSom!.data as SpyOnMilitaryData).defensePoints!, 0);

  const armiesOut = scoutedSom.filter((p) => (p.latestSom!.data as SpyOnMilitaryData).hasArmiesOut === true).length;

  return {
    cells: [
      { label: 'Total Raw Off.', value: fmtOrDash(scoutedCount, totalRawOff), color: OFF_CLR },
      { label: 'Total Raw Def.', value: fmtOrDash(scoutedCount, totalRawDef), color: COLORS.link },
      { label: 'Armies Out', value: fmtOrDash(scoutedCount, armiesOut), color: COLORS.warning },
    ],
    scoutedCount,
    scoutedOf,
    caveat: caveatText(scoutedCount, scoutedOf),
  };
}

function computeEffectsTotals(provinces: DossierProvinceRow[]): DossierTotals {
  const scoutedOf = provinces.length;
  const scouted = provinces.filter((p) => p.latestCb != null);
  const scoutedCount = scouted.length;

  const hasCategory = (p: DossierProvinceRow, category: 'DEFENSE' | 'DEBUFF' | 'BUFF') =>
    (p.latestCb?.data as CrystalBallData | undefined)?.activeEffects?.some(
      (e) => classifyEffect(e.effectType) === category
    ) ?? false;

  const hardened = provinces.filter((p) => hasCategory(p, 'DEFENSE')).length;
  const weakened = provinces.filter((p) => hasCategory(p, 'DEBUFF')).length;
  const boosted = provinces.filter((p) => hasCategory(p, 'BUFF')).length;

  return {
    cells: [
      { label: 'Hardened (Def.)', value: fmtOrDash(scoutedCount, hardened), color: COLORS.negative },
      { label: 'Weakened (Deb.)', value: fmtOrDash(scoutedCount, weakened), color: COLORS.positive },
      { label: 'Boosted (Buf.)', value: fmtOrDash(scoutedCount, boosted), color: COLORS.warning },
    ],
    scoutedCount,
    scoutedOf,
    caveat: caveatText(scoutedCount, scoutedOf),
  };
}

/** Computes the active tab's kingdom-wide totals row from the roster. */
export function computeDossierTotals(tab: DossierTabId, provinces: DossierProvinceRow[]): DossierTotals {
  switch (tab) {
    case 'resources': return computeResourcesTotals(provinces);
    case 'military': return computeMilitaryTotals(provinces);
    case 'effects': return computeEffectsTotals(provinces);
  }
}
